import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { pdfjs } from 'react-pdf';

import { errorMsg } from 'lib/toastNotification';
import { store, persistor } from 'store/store';

import App from './App';
import reportWebVitals from './reportWebVitals';

import './index.css';
import 'react-loading-skeleton/dist/skeleton.css';
import 'rsuite/dist/rsuite.min.css';
import 'react-day-picker/dist/style.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// ===== PDF worker =====
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// ===== ENV =====
const SENTRY_DSN = process.env.REACT_APP_SENTRY_ADMIN;
const APP_ENV = process.env.REACT_APP_ENV || process.env.NODE_ENV;

// ===== React Query =====
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchInterval: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => errorMsg(error),
  }),
});

// ===== Render helper =====
const root = ReactDOM.createRoot(document.getElementById('root'));

let Sentry = null;

function render() {
  const appTree = (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          {Sentry ? (
            <Sentry.ErrorBoundary fallback={<div>Oops, something went wrong.</div>}>
              <App />
            </Sentry.ErrorBoundary>
          ) : (
            <App />
          )}
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  );

  root.render(appTree);
}

async function initSentryIfProd() {
  const isProd = process.env.NODE_ENV === 'production';
  const hasDSN = !!SENTRY_DSN;

  if (!isProd || !hasDSN) return;

  const mod = await import('@sentry/react');
  Sentry = mod;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: APP_ENV,
    tracesSampleRate: 0.2,
    sendDefaultPii: true,
  });
}

window.addEventListener('unhandledrejection', (event) => {
  const message = event.reason?.message || '';
  if (message.includes('Loading chunk') || message.includes('ChunkLoadError')) {
    if (Sentry) Sentry.captureException(event.reason);
    window.location.reload();
  }
});

async function init() {
  await initSentryIfProd();
  render();
}

init();

// Web vitals
reportWebVitals((metric) => {
  if (process.env.NODE_ENV !== 'production' || !Sentry) return;
  Sentry.addBreadcrumb({
    category: 'web-vitals',
    level: 'info',
    message: `${metric.name}=${Math.round(metric.value)}`,
  });
});
